import React, { useState } from 'react';
import { Modal, Button, Form, Spinner, Alert } from '@/components/ui';
import { Task } from '../../../types/Task';

interface PrintTaskPDFModalProps {
  task: Task | null;
  onClose: () => void;
}

const PrintTaskPDFModal: React.FC<PrintTaskPDFModalProps> = ({ task, onClose }) => {
  const [includeTimecards, setIncludeTimecards] = useState(false);
  const [includeVendorInvoices, setIncludeVendorInvoices] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = async () => {
    if (!task) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/pdf/generate-task-pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Only the id goes over the wire: the server reads the task from the database, so the
        // report no longer depends on which list screen's `select` populated this row.
        body: JSON.stringify({ taskId: task.id, includeTimecards, includeVendorInvoices }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body?.error || 'Failed to generate PDF');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Task_${task.taskNumber || task.id}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate PDF. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={task !== null} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-file-earmark-pdf tw-mr-2 tw-text-danger" />
          Print Task PDF
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {task && (
          <p className="tw-text-content-muted tw-text-sm tw-mb-3">
            <strong>{task.taskNumber || `Task #${task.id}`}</strong>
            {task.name ? ` — ${task.name}` : ''}
          </p>
        )}

        <p className="tw-mb-3">
          The PDF includes a Cost &amp; Billables Summary, Labour Breakdown (straight time &amp; overtime),
          Equipment Breakdown (base rate &amp; fuel surcharge), and detailed tables for all cost categories.
        </p>

        <Form>
          <Form.Group className="tw-mb-3">
            <Form.Label className="tw-font-semibold">Optional Appendices</Form.Label>
            <Form.Check
              type="checkbox"
              id="include-timecards"
              label="Include time cards (Daily Log forms for labour &amp; equipment)"
              checked={includeTimecards}
              onChange={(e) => setIncludeTimecards(e.target.checked)}
            />
            <Form.Check
              type="checkbox"
              id="include-vendor-invoices"
              label="Include sub / hired equipment invoices"
              checked={includeVendorInvoices}
              onChange={(e) => setIncludeVendorInvoices(e.target.checked)}
              className="tw-mt-1"
            />
          </Form.Group>
        </Form>

        {error && (
          <Alert variant="danger" className="tw-py-2 tw-text-sm">{error}</Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleGenerate} disabled={loading}>
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="tw-mr-2" />
              Generating…
            </>
          ) : (
            <>
              <i className="bi bi-download tw-mr-2" />
              Generate PDF
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PrintTaskPDFModal;
